import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import { css } from "react-emotion";
import moment from "moment-timezone";
import DateRangePicker from "react-dates/lib/components/DateRangePicker";
import { Button, Card, CardBody, CardHeader } from "reactstrap";
import LoadingOverlay from "@/views/Components/LoadingOverlay";
import reports from "@/api/reports";
import { saveAs } from "file-saver";
import { handleError } from "@/util/errorHandler";
import { APP_CONST } from "@/store/dataStore";
import { omit } from "lodash";

const modalStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  overflow: visible;
  margin-right: -50%;
  transform: translate(-50%, -50%);

  .buttons {
    margin: 15px 0 0;
    text-align: right;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 1021; /* above navs, below sweetalert */
`;

class ExportReportModal extends React.Component {
  state = {
    dateFrom: null,
    dateTo: null,
    focusedInput: null,
    loading: false,
  };

  static getDerivedStateFromProps({
    modalIsOpen,
    initialDates: { dateFrom, dateTo },
  }) {
    if (!modalIsOpen) {
      return { dateFrom, dateTo };
    }

    return null;
  }

  handleExportClicked = async () => {
    const formatDate = (d, format) =>
      d ? moment(d).format(format) : undefined;
    const params = {
      ...omit(this.props.params, ["page", "size"]),
      from: formatDate(this.state.dateFrom, APP_CONST.DATE_FROM),
      to: formatDate(this.state.dateTo, APP_CONST.DATE_TO),
    };
    let reportId, content;

    this.setState({ loading: true });

    try {
      ({ reportId } = await this.props.requestReport(params));
    } catch (err) {
      handleError(err, { title: `failed in requesting a report` });
      this.setState({ loading: false });
      return;
    }

    try {
      await reports.waitUntilProcessed(reportId);
    } catch (err) {
      handleError(err, { title: `failed in processing a report ${reportId}` });
      this.setState({ loading: false });
      return;
    }

    try {
      content = await reports.getReport(reportId);
    } catch (err) {
      handleError(err, { title: `failed in downloading a report ${reportId}` });
      this.setState({ loading: false });
      return;
    }

    saveAs(new Blob([content]), `report-${reportId}.csv`);

    this.setState({ loading: false });
    this.props.onRequestClose();
  };

  render() {
    return (
      <Modal
        isOpen={this.props.modalIsOpen}
        onRequestClose={this.props.onRequestClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <LoadingOverlay loading={this.state.loading} />

          <CardHeader>
            <Button close onClick={this.props.onRequestClose} />

            <span>Export a report</span>
          </CardHeader>

          <CardBody>
            <div>
              <DateRangePicker
                startDate={this.state.dateFrom}
                startDateId="date_from"
                endDate={this.state.dateTo}
                endDateId="date_to"
                onDatesChange={({ startDate, endDate }) =>
                  this.setState({ dateFrom: startDate, dateTo: endDate })
                }
                focusedInput={this.state.focusedInput}
                onFocusChange={focusedInput => this.setState({ focusedInput })}
                showClearDates
                showDefaultInputIcon
                isOutsideRange={date =>
                  moment(date).isAfter(
                    moment.tz("Asia/Phnom_Penh").endOf("day"),
                  )
                }
                small
                minimumNights={0}
                disabled={this.props.disableDateRangePicker}
              />
            </div>

            <div className="buttons">
              <Button color="primary" onClick={this.handleExportClicked}>
                Export CSV
              </Button>
            </div>
          </CardBody>
        </Card>
      </Modal>
    );
  }
}

ExportReportModal.defaultProps = {
  params: {},
  requestReport: () => {},
  modalIsOpen: false,
  onRequestClose: () => {},
  disableDateRangePicker: false,
  initialDates: {
    dateFrom: null,
    dateTo: null,
  },
};

ExportReportModal.propTypes = {
  params: PropTypes.object.isRequired,
  requestReport: PropTypes.func.isRequired,
  modalIsOpen: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  disableDateRangePicker: PropTypes.bool,
  initialDates: PropTypes.shape({
    dateFrom: PropTypes.object,
    dateTo: PropTypes.object,
  }),
};

export default ExportReportModal;
